import { useQuery } from '@tanstack/react-query';
import { useTRPC } from 'src/utils/trpc';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';

/**
 * Every conversation thread hanging off one node, oldest first, comments
 * included (CUS-243).
 *
 * tRPC-only, and deliberately so rather than as a shortcut. The only writer of
 * these threads is the data-layer status processor, which is reached ONLY on
 * the V3 path — with `trpc` off, `useUpdateThirdPartyResponseStatus` calls the
 * legacy Hasura action instead and nothing is ever persisted. A GraphQL leg
 * could therefore only ever return an empty list, so building one would add a
 * Hasura relationship, permissions and codegen surface to display nothing.
 *
 * Flag OFF → the query stays disabled and this returns an empty list, which
 * callers render as "no history" (the block renders nothing at all).
 */
export const useGetConversationsByParentId = (parentId?: string | null) => {
  const trpc = useTRPC();
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');
  const enabled = trpcEnabled && !!parentId;

  const { data, isLoading } = useQuery({
    ...trpc.frontend.conversation.byParentId.queryOptions({
      parentId: parentId ?? '',
    }),
    enabled,
  });

  return { conversations: data ?? [], loading: enabled && isLoading };
};
